import { useLayoutEffect, useRef } from 'react';
import { Star, Quote, ExternalLink } from 'lucide-react';
import { gsap, revealUp } from '../lib/motion';

const REVIEWS = [
  {
    author: 'Sarah Johnson',
    when: '2 months ago',
    text: 'Revive Builders did an amazing job renovating our kitchen! The attention to detail and quality of work exceeded our expectations. Highly recommend them for any renovation project.',
  },
  {
    author: 'Mike Rodriguez',
    when: '1 month ago',
    text: 'Professional, reliable, and excellent craftsmanship. They transformed our outdated bathroom into a modern masterpiece. The team was courteous and completed the project on time.',
  },
  {
    author: 'Jennifer Lee',
    when: '3 weeks ago',
    text: 'Outstanding service from start to finish. The renovation of our living space was handled with great care and expertise. We couldn’t be happier with the results!',
  },
  {
    author: 'David Wilson',
    when: '2 weeks ago',
    text: 'Top-notch renovation company! They helped us renovate our commercial space and the results are fantastic. Professional, efficient, and great communication throughout the process.',
  },
];

export default function Reviews() {
  const ref = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      revealUp('.rev-head', el);
      revealUp('.rev-card', el, {
        scrollTrigger: { trigger: el.querySelector('.rev-grid'), start: 'top 82%' },
        stagger: 0.1,
      });
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={ref} id="reviews" className="relative bg-ink-900 py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="rev-head mb-16 text-center">
          <p className="eyebrow mb-6">Reviews</p>
          <h2 className="font-display text-4xl font-medium text-steel-100 md:text-5xl">
            Word travels.
          </h2>
          <div className="mt-6 flex items-center justify-center gap-1.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-gold-400 text-gold-400" />
            ))}
            <span className="ml-2 text-sm text-steel-300">5.0 on Google</span>
          </div>
        </div>

        <div className="rev-grid grid grid-cols-1 gap-5 md:grid-cols-2">
          {REVIEWS.map((r) => (
            <figure key={r.author} className="rev-card glass rounded-2xl p-8">
              <Quote className="h-5 w-5 text-gold-400/70" />
              <blockquote className="mt-4 text-sm font-light leading-relaxed text-steel-200 md:text-base">
                {r.text}
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold-500/20 font-display text-gold-300">
                  {r.author.charAt(0)}
                </span>
                <span>
                  <span className="block text-sm font-medium text-steel-100">{r.author}</span>
                  <span className="block text-xs text-steel-400">{r.when}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="rev-head mt-14 text-center">
          <a
            href="https://g.page/r/CTJUT9-6Wx6GEAE/review"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-steel-100/15 px-7 py-3 text-sm text-steel-200 transition-all duration-300 hover:border-gold-500/60 hover:text-gold-300"
          >
            <ExternalLink className="h-4 w-4" />
            View all reviews on Google
          </a>
        </div>
      </div>
    </section>
  );
}
